/**
 * Xero Configuration API Service
 *
 * Handles all API calls for Xero integration configuration:
 * - Save / update configuration (credentials + scopes + defaults)
 * - Get existing configuration (secret masked)
 * - Delete configuration
 * - Check configuration status
 * - Get reference data (account codes + tax types)
 *
 * No Redux — the XeroConfigPage manages its own async state.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Configuration CRUD
// ============================================

/**
 * Save or update Xero configuration for the authenticated organisation.
 *
 * @param {{
 *   clientId: string,
 *   clientSecret: string,
 *   allowedApiScopes: string[],
 *   defaultAccountCode: string,
 *   defaultTaxType: string
 * }} config
 * @returns {Promise<XeroConfigResponse>}
 */
export const saveXeroConfigApi = async (config) => {
    const response = await axiosInstance.post('/integrations/xero/configure', config);
    return response.data;
};

/**
 * Get current Xero configuration for the authenticated organisation.
 * Client secret is masked in the response.
 *
 * @returns {Promise<XeroConfigResponse>}
 */
export const getXeroConfigApi = async () => {
    const response = await axiosInstance.get('/integrations/xero/configuration');
    return response.data;
};

/**
 * Delete Xero configuration for the authenticated organisation.
 *
 * @returns {Promise<{ organisationUuid: string, message: string }>}
 */
export const deleteXeroConfigApi = async () => {
    const response = await axiosInstance.delete('/integrations/xero/configuration');
    return response.data;
};

// ============================================
// Status
// ============================================

/**
 * Check if Xero is configured for the authenticated organisation.
 *
 * @returns {Promise<{ organisationUuid: string, configured: boolean }>}
 */
export const getXeroStatusApi = async () => {
    const response = await axiosInstance.get('/integrations/xero/status');
    return response.data;
};

// ============================================
// Reference Data
// ============================================

/**
 * Get available account codes and tax types for the Xero config UI.
 *
 * @returns {Promise<{ accountCodes: AccountCodeOption[], taxTypes: TaxTypeOption[] }>}
 */
export const getXeroReferenceDataApi = async () => {
    const response = await axiosInstance.get('/integrations/xero/reference-data');
    return response.data;
};

// ============================================
// Default Export
// ============================================

const xeroApi = {
    saveXeroConfigApi,
    getXeroConfigApi,
    deleteXeroConfigApi,
    getXeroStatusApi,
    getXeroReferenceDataApi,
};

export default xeroApi;